import Button from "../button";
import { Calendar, ExternalLink, Import, LoopIcon, Plus } from "../../assets/icons";
import useSlideOver from "../slideOver/useSlideOver";
import StyledInput from "../styledInput/StyledInput";
import {
  forceDateToUtc0,
  datePeriodShort,
  todayAtLocalCalendarNoon,
  toLocalCalendarNoon,
  isLocalCalendarTodayRange,
  isSameLocalCalendarDay,
} from "../../utils/Date";
import RangeDatePicker from "../datePicker/RangeDatePicker";

const NavBarHourlyTracking = ({
  dateStart,
  setDateStart,
  dateEnd,
  setDateEnd,
  companySelected,
  interimAgencyList,
  setInterimAgencyList,
  interimAgencySelected,
  setInterimAgencySelected,
  interimSectorList,
  setInterimSectorList,
  interimSectorSelected,
  setInterimSectorSelected,
  search,
  setSearch,
  missionStatusSelected,
  setMissionStatusSelected,
  missionStatusList,
  setMissionStatusList,
  qualificationList = [],
  setQualificationList,
  qualificationSelected = [],
  setQualificationSelected,
  onCreateMission,
}) => {
  const { toggleSlideOver } = useSlideOver();
  const isShowImportExport = [225, 215, 210].includes(
    Number(localStorage.getItem("rUser")) || 0,
  );

  const isToday = isLocalCalendarTodayRange(dateStart, dateEnd);

  const isSingleDay = isSameLocalCalendarDay(dateStart, dateEnd);

  const periodLabel = dateStart && dateEnd && !isSingleDay
    ? datePeriodShort(dateStart, dateEnd)
    : null;

  const updateInterimAgenciesList = (updatedInterimAgency) => {
    const updatedInterimAgencies = [];

    for (let i = 0; i < interimAgencyList.length; i++) {
      for (let j = 0; j < updatedInterimAgency.length; j++) {
        if (interimAgencyList[i].name === updatedInterimAgency[j].value) {
          updatedInterimAgencies.push({
            ...interimAgencyList[i],
            checked: updatedInterimAgency[j].checked,
          });
        }
      }
    }

    setInterimAgencyList(updatedInterimAgencies);

    const selectedInterimAgencies = updatedInterimAgencies.filter(
      (interimAgency) => interimAgency.checked,
    );

    setInterimAgencySelected(selectedInterimAgencies);
  };

  const updateInterimSectorsList = (updatedInterimSector) => {
    // Sectors are now identified by `_id` to support multi-source data ; legacy `name`
    // matching is preserved as a fallback for stores not yet migrated.
    const updatedInterimSectors = [];

    for (let i = 0; i < interimSectorList.length; i++) {
      for (let j = 0; j < updatedInterimSector.length; j++) {
        const matchById =
          interimSectorList[i]._id &&
          interimSectorList[i]._id === updatedInterimSector[j].value;
        const matchByName =
          interimSectorList[i].name === updatedInterimSector[j].value;
        if (matchById || matchByName) {
          updatedInterimSectors.push({
            ...interimSectorList[i],
            checked: updatedInterimSector[j].checked,
          });
        }
      }
    }

    setInterimSectorList(updatedInterimSectors);

    const selectedInterimSectors = updatedInterimSectors.filter(
      (interimSector) => interimSector.checked,
    );

    setInterimSectorSelected(selectedInterimSectors);
  };

  const updateQualificationsList = (updated) => {
    const next = [];
    for (let i = 0; i < qualificationList.length; i++) {
      for (let j = 0; j < updated.length; j++) {
        if (qualificationList[i]._id === updated[j].value) {
          next.push({ ...qualificationList[i], checked: updated[j].checked });
        }
      }
    }
    setQualificationList?.(next);
    setQualificationSelected?.(next.filter((q) => q.checked));
  };

  const updateMissionStatusList = (updateMissionStatus) => {
    const updatedMissionsStatus = [];

    for (let i = 0; i < missionStatusList.length; i++) {
      for (let j = 0; j < updateMissionStatus.length; j++) {
        if (missionStatusList[i].type === updateMissionStatus[j].value) {
          updatedMissionsStatus.push({
            ...missionStatusList[i],
            checked: updateMissionStatus[j].checked,
          });
        }
      }
    }

    setMissionStatusList(updatedMissionsStatus);

    const selectedMissionStatus = updatedMissionsStatus.filter(
      (missionStatus) => missionStatus.checked,
    );

    setMissionStatusSelected(selectedMissionStatus);
  };

  const exportMissions = () => {
    if (companySelected) {
      toggleSlideOver("export_missions");
    }
  };

  return (
    <nav className={"w-full flex justify-between items-center min-h-[45px]"}>
      <div className={"flex gap-2 items-center text-sm"}>
        <Button
          color={isToday ? "DISABLED" : "SECONDARY"}
          size={"SMI"}
          onClick={!isToday ? () => { setDateStart(todayAtLocalCalendarNoon()); setDateEnd(todayAtLocalCalendarNoon()); } : undefined}
        >
          Aujourd'hui
        </Button>
        <div>
          <RangeDatePicker
            startDate={dateStart ? forceDateToUtc0(dateStart) : null}
            endDate={dateEnd ? forceDateToUtc0(dateEnd) : null}
            onApply={({ start, end }) => {
              if (start) setDateStart(toLocalCalendarNoon(start));
              if (end) setDateEnd(toLocalCalendarNoon(end));
              else setDateEnd(null);
            }}
            dateFormat="dd/MM/yyyy"
            locale="fr"
            peekNextMonth
            dropdownMode="select"
            popperPlacement="bottom"
            className="w-full h-9 pl-3 pr-8 ft-b border rounded-lg text-center"
            customInput={
              <Button
                color={periodLabel ? "PRIMARY" : "SECONDARY"}
                size={"ICON"}
                name="header_hourly_tracking_range_calendar"
                title={
                  periodLabel
                    ? `${periodLabel} — Cliquer pour modifier la période`
                    : "Choisir une période"
                }
              >
                <Calendar
                  wh={16}
                  color={periodLabel ? "#FFFFFF" : "#374151"}
                />
              </Button>
            }
          />
        </div>
        {interimAgencyList.length > 1 && (
          <div className={"max-w-[150px] text-sm"}>
            <StyledInput
              type={"selectMultipleCheckbox"}
              value={
                interimAgencySelected.length > 0
                  ? interimAgencySelected
                      .map((agency) => agency.name)
                      .join(", ")
                  : "Agence"
              }
              list={interimAgencyList.map((interimAgency) => {
                return {
                  value: interimAgency.name,
                  label: interimAgency.name,
                  checked: interimAgency.checked,
                };
              })}
              onChange={(updatedInterimAgency) =>
                updateInterimAgenciesList(updatedInterimAgency)
              }
              isSearchable={true}
            />
          </div>
        )}
        {interimSectorList.length > 1 && (
          <div className={"max-w-[150px] text-sm"}>
            <StyledInput
              type={"selectMultipleCheckbox"}
              value={
                interimSectorSelected.length > 0
                  ? interimSectorSelected
                      .map((sector) => sector.name)
                      .join(", ")
                  : "Secteurs"
              }
              list={interimSectorList.map((interimSector) => {
                return {
                  value: interimSector._id || interimSector.name,
                  label: interimSector.name,
                  checked: interimSector.checked,
                };
              })}
              onChange={(updatedInterimSector) =>
                updateInterimSectorsList(updatedInterimSector)
              }
              isSearchable={true}
            />
          </div>
        )}
        {qualificationList.length > 1 && (
          <div className={"max-w-[150px] text-sm"}>
            <StyledInput
              type={"selectMultipleCheckbox"}
              value={
                qualificationSelected.length > 0
                  ? qualificationSelected.map((q) => q.name).join(", ")
                  : "Postes"
              }
              list={qualificationList.map((q) => ({
                value: q._id,
                label: q.name,
                checked: q.checked,
              }))}
              onChange={(updated) => updateQualificationsList(updated)}
              isSearchable={true}
            />
          </div>
        )}
        {missionStatusList.length > 1 && (
          <div className={"max-w-[150px] text-sm"}>
            <StyledInput
              type={"selectMultipleCheckbox"}
              value={
                missionStatusSelected.length > 0
                  ? missionStatusSelected
                      .map((status) => status.name)
                      .join(", ")
                  : "État"
              }
              list={missionStatusList.map((missionStatus) => {
                return {
                  value: missionStatus.type,
                  label: missionStatus.name,
                  checked: missionStatus.checked,
                };
              })}
              onChange={(updatedMissionStatus) =>
                updateMissionStatusList(updatedMissionStatus)
              }
            />
          </div>
        )}
        <div className={"w-[213px] text-sm"}>
          <StyledInput
            type={"text"}
            placeholder={"Rechercher..."}
            imgFront={<LoopIcon wh={16} color={"#6B7280"} />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </div>
      </div>
      {isShowImportExport && (
        <div className={"flex gap-2 items-center"}>
          {onCreateMission && (
            <Button
              color={"PRIMARY"}
              size={"SM"}
              onClick={onCreateMission}
              name="header_create_mission"
              title="Créer une mission"
            >
              <div className={"flex gap-1 items-center"}>
                <Plus wh={16} color={"#FFFFFF"} />
                Créer
              </div>
            </Button>
          )}
          <Button
            color={"SECONDARY"}
            size={"ICON"}
            onClick={() => toggleSlideOver("import_missions")}
            name="header_import_missions"
            title="Importer des missions"
          >
            <Import wh={16} color={"#374151"} />
          </Button>
          {companySelected ? (
            <div className={"flex flex-col"}>
              <div className={"flex justify-end"}>
                <div className={"w-fit"}>
                  <Button
                    color={"SECONDARY"}
                    size={"SM"}
                    onClick={exportMissions}
                  >
                    <div className={"flex gap-1 items-center"}>
                      <ExternalLink wh={16} color={"#374151"} />
                      Export
                    </div>
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <Button color={"DISABLED"} size={"SMI"}>
              <div className={"flex gap-1 items-center"}>
                <ExternalLink wh={16} color={"#9CA3AF"} />
                Export
              </div>
            </Button>
          )}
        </div>
      )}
    </nav>
  );
};

export default NavBarHourlyTracking;
